import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './CreateCircle.css';
import NotificationBell from './NotificationBell';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const ManageCircle = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [circle, setCircle] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [suspended, setSuspended] = useState(false);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  // Transfer ownership
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newCircleName, setNewCircleName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Remove member
  const [removingId, setRemovingId] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const fetchCircle = useCallback(async () => {
    try {
      const [circleRes, statusRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/circles/mine`, { headers: authHeaders }),
        fetch(`${process.env.REACT_APP_API_URL}/api/billing/circle-status`, { headers: authHeaders }),
      ]);

      const data = await circleRes.json();
      if (!circleRes.ok) { setError(data.error || 'Failed to load Circle.'); return; }
      setCircle(data.circle);
      setUserRole(data.userRole);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setSuspended(statusData.suspended || false);
        setInGracePeriod(statusData.inGracePeriod || false);
      }
    } catch (err) {
      console.error('[ManageCircle] fetch:', err);
      setError('Failed to load Circle.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCircle();
  }, [fetchCircle]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/invite`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setInviteError(data.error || 'Failed to send invite.');
      } else {
        setInviteSuccess(`Invite sent to ${inviteEmail.trim()}`);
        setInviteEmail('');
        fetchCircle();
      }
    } catch (err) {
      setInviteError('Failed to send invite. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (email) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/resend-invite`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ email }),
        }
      );
      setInviteSuccess(`Invite resent to ${email}`);
      setTimeout(() => setInviteSuccess(null), 3000);
    } catch (err) {
      console.error('[ManageCircle] resend:', err);
    }
  };

  const handleCancelInvite = async (email) => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/invites/${encodeURIComponent(email)}`,
        { method: 'DELETE', headers: authHeaders }
      );
      fetchCircle();
    } catch (err) {
      console.error('[ManageCircle] cancel invite:', err);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!memberId || memberId === 'undefined') return;
    if (!window.confirm('Remove this member from the Circle?')) return;
    setRemovingId(memberId);
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/members/${memberId}`,
        { method: 'DELETE', headers: authHeaders }
      );
      fetchCircle();
    } catch (err) {
      console.error('[ManageCircle] remove member:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleSaveName = async () => {
    if (!newCircleName.trim() || newCircleName.trim() === circle.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/name`,
        {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ name: newCircleName.trim() }),
        }
      );
      if (res.ok) {
        setEditingName(false);
        fetchCircle();
      }
    } catch (err) {
      console.error('[ManageCircle] save name:', err);
    } finally {
      setSavingName(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    if (!window.confirm('Transfer Circle ownership? You will become a regular member.')) return;
    setTransferring(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/circles/${circle._id}/transfer`,
        {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ newOwnerId: transferTo }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Transfer failed.');
      } else {
        setShowTransfer(false);
        fetchCircle();
      }
    } catch (err) {
      console.error('[ManageCircle] transfer:', err);
    } finally {
      setTransferring(false);
    }
  };

  const getInitials = (member) => {
    const u = member.userId;
    if (!u) return '?';
    const first = u.firstname?.[0] || '';
    const last = u.lastname?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    // Fallback to first letter of email or username
    return (u.email?.[0] || u.username?.[0] || '?').toUpperCase();
  };

  const isOwnerOrAdmin = ['owner', 'admin'].includes(userRole);
  const activePendingInvites = circle?.pendingInvites?.filter(
    i => new Date(i.expiresAt) > new Date()
  ) || [];

  if (loading) {
    return (
      <div className="circle-page">
        <div className="circle-container">
          <div className="circle-loading">Loading your Circle...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="circle-page">
        <div className="circle-container">
          <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <div className="circle-error">⚠️ {error}</div>
          <button className="create-circle-btn" onClick={() => navigate('/circles/create')} style={{marginTop:'1rem'}}>
            Create a Circle
          </button>
        </div>
      </div>
    );
  }

  const seatsUsed = circle.members.length;
  const seatsTotal = circle.maxMembers;
  const seatsRemaining = seatsTotal - seatsUsed;
  const seatPct = Math.round((seatsUsed / seatsTotal) * 100);

  return (
    <div className="circle-page">
      <div className="circle-container">

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          {circle && <NotificationBell circleId={circle._id} />}
        </div>

        {/* Grace period warning */}
        {inGracePeriod && (
          <div className="circle-warning-banner">
            ⚠️ Your Circle subscription has lapsed. You have a 7-day grace period — please update your billing to keep your Circle active.
            <a href="/settings" style={{marginLeft:'0.5rem', fontWeight:600, color:'#92400E'}}>Update billing →</a>
          </div>
        )}

        {/* Suspended banner */}
        {suspended && !inGracePeriod && (
          <div className="circle-suspended-banner">
            🔒 This Circle has been suspended due to a billing issue. Members cannot access shared clients until the subscription is renewed.
            <a href="/settings" style={{marginLeft:'0.5rem', fontWeight:600, color:'#7F1D1D'}}>Renew subscription →</a>
          </div>
        )}

        {/* Downgrade warning — show when at capacity on Circle 3 or 5 */}
        {userRole === 'owner' && circle && !suspended && (
          circle.plan === 'circle_3' && circle.members.length >= 3 ? (
            <div className="circle-warning-banner">
              ⚠️ Your Circle is full (3/3 members). To downgrade from Circle 3, you must remove a member first.
            </div>
          ) : circle.plan === 'circle_5' && circle.members.length > 3 ? (
            <div className="circle-warning-banner">
              ⚠️ You have {circle.members.length} members. To downgrade to Circle 3, remove {circle.members.length - 3} member{circle.members.length - 3 > 1 ? 's' : ''} first.
            </div>
          ) : circle.plan === 'circle_5' && circle.members.length >= 5 ? (
            <div className="circle-warning-banner">
              ⚠️ Your Circle is full (5/5 members). To downgrade from Circle 5, you must remove a member first.
            </div>
          ) : null
        )}

        {/* Header */}
        <div className="circle-header">
          <div className="circle-sparkle">✦</div>
          {editingName ? (
            <div className="circle-name-edit">
              <input
                className="circle-name-input"
                value={newCircleName}
                onChange={e => setNewCircleName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus
                maxLength={100}
              />
              <div className="circle-name-edit-actions">
                <button className="invite-btn" onClick={handleSaveName} disabled={savingName}>
                  {savingName ? 'Saving...' : 'Save'}
                </button>
                <button className="cancel-transfer-btn" onClick={() => setEditingName(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="circle-name-row">
              <h1>{circle.name}</h1>
              {userRole === 'owner' && (
                <button
                  className="circle-name-edit-btn"
                  onClick={() => { setNewCircleName(circle.name); setEditingName(true); }}
                  title="Edit circle name"
                >
                  ✎
                </button>
              )}
            </div>
          )}
          <span className="circle-plan-badge">
            {circle.plan.replace('_', ' ').replace('circle', 'Circle')} · {seatsUsed}/{seatsTotal} members
          </span>
        </div>

        {/* Seat meter */}
        <div className="seat-meter">
          <div className="seat-meter-bar">
            <div className="seat-meter-fill" style={{ width: `${seatPct}%` }} />
          </div>
          <p className="seat-meter-label">
            {seatsRemaining > 0
              ? `${seatsRemaining} seat${seatsRemaining !== 1 ? 's' : ''} remaining`
              : 'Circle is full'}
          </p>
        </div>

        {/* Invite Section — owner/admin only */}
        {isOwnerOrAdmin && seatsRemaining > 0 && (
          <div className="manage-section">
            <h2 className="section-label">Invite a member</h2>
            <div className="invite-row">
              <input
                type="email"
                className="circle-name-input"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                disabled={inviting}
              />
              <select
                className="role-select"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button
                className="invite-btn"
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
              >
                {inviting ? 'Sending...' : 'Send invite'}
              </button>
            </div>
            {inviteSuccess && <p className="invite-success">✓ {inviteSuccess}</p>}
            {inviteError && <p className="invite-error">⚠️ {inviteError}</p>}
          </div>
        )}

        {/* Members List */}
        <div className="manage-section">
          <h2 className="section-label">Members</h2>
          <div className="members-list">
            {circle.members
              .filter(member => member.userId) // skip orphaned members
              .map((member) => {
              const u = member.userId;
              const memberId = u?._id?.toString() || u?.id?.toString() || u?.toString();
              const isMe = memberId === localStorage.getItem('userId')?.toString();
              const displayName = [u?.firstname, u?.lastname].filter(Boolean).join(' ') || u?.username || u?.email || 'Member';
              return (
                <div key={member._id} className="member-row">
                  <div className="member-avatar">{getInitials(member)}</div>
                  <div className="member-info">
                    <p className="member-name">
                      {displayName}
                      {isMe && <span className="you-badge"> (you)</span>}
                    </p>
                    <p className="member-email">{u?.email}</p>
                  </div>
                  <div className="member-right">
                    <span className={`role-badge role-${member.role}`}>
                      {ROLE_LABELS[member.role]}
                    </span>
                    {isOwnerOrAdmin && !isMe && member.role !== 'owner' && (
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveMember(memberId)}
                        disabled={removingId === memberId}
                      >
                        {removingId === memberId ? '...' : 'Remove'}
                      </button>
                    )}
                    {isMe && member.role !== 'owner' && (
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveMember(memberId)}
                      >
                        Leave
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pending Invites */}
        {isOwnerOrAdmin && activePendingInvites.length > 0 && (
          <div className="manage-section">
            <h2 className="section-label">Pending invites</h2>
            <div className="members-list">
              {activePendingInvites.map((invite) => (
                <div key={invite._id} className="member-row">
                  <div className="member-avatar pending-avatar">✉</div>
                  <div className="member-info">
                    <p className="member-name">{invite.email}</p>
                    <p className="member-email">
                      Sent {new Date(invite.sentAt).toLocaleDateString()} ·
                      Expires {new Date(invite.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="member-right">
                    <span className="role-badge role-pending">Pending</span>
                    <button
                      className="resend-btn"
                      onClick={() => handleResend(invite.email)}
                    >
                      Resend
                    </button>
                    <button
                      className="remove-btn"
                      onClick={() => handleCancelInvite(invite.email)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transfer Ownership — owner only */}
        {userRole === 'owner' && (
          <div className="manage-section danger-adjacent">
            <h2 className="section-label">Transfer ownership</h2>
            {!showTransfer ? (
              <button className="transfer-btn" onClick={() => setShowTransfer(true)}>
                Transfer Circle ownership
              </button>
            ) : (
              <div className="transfer-form">
                <p className="transfer-warning">
                  ⚠️ The new owner will take over billing responsibility (${
                    circle.plan === 'circle_3' ? '79' :
                    circle.plan === 'circle_5' ? '129' : '199'
                  }/mo). You will become a regular member.
                </p>
                <select
                  className="role-select"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                >
                  <option value="">Select new owner...</option>
                  {circle.members
                    .filter(m => m.role !== 'owner')
                    .map(m => (
                      <option key={m._id} value={m.userId?._id}>
                        {m.userId?.firstname} {m.userId?.lastname} ({m.userId?.email})
                      </option>
                    ))}
                </select>
                <div className="transfer-actions">
                  <button
                    className="create-circle-btn"
                    onClick={handleTransfer}
                    disabled={!transferTo || transferring}
                    style={{ marginTop: 0 }}
                  >
                    {transferring ? 'Transferring...' : 'Confirm transfer'}
                  </button>
                  <button
                    className="cancel-transfer-btn"
                    onClick={() => setShowTransfer(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Shared Clients + Calendar shortcuts */}
        <div className="manage-section">
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              className="view-clients-btn"
              onClick={() => navigate(`/circles/${circle._id}/clients`)}
              style={{ flex: 1 }}
            >
              👥 Shared clients →
            </button>
            <button
              className="view-clients-btn"
              onClick={() => navigate(`/circles/${circle._id}/calendar`)}
              style={{ flex: 1 }}
            >
              🗓 Circle calendar →
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageCircle;
